import { makeAutoObservable, runInAction } from 'mobx'
import { v4 as uuid } from 'uuid'
import agent from '../api/agent'
import { serializeTextBoxes } from '../utils/shootsCoords'

/**
 * Shoot text overlay (editor in-memory shape; POST body via v2 wrapper).
 * All spatial fields are normalized 0–1 fractions of media dimensions: x, y, width, height are
 * fractions of media width or height as applicable; fontSize is a fraction of media height.
 */
export interface Textbox {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Fraction of media height (e.g. 0.04 ≈ 4% of media height). */
  fontSize: number;
  /** Default Arial; more families in v0.39 */
  fontFamily: string;
  /** Hex; default #FFFFFF */
  color: string;
  /** 0–1; default 1 */
  opacity: number;
  /** Degrees; 0 default; rotation UI reserved for v0.39 */
  rotation: number;
}

export type ShootMediaType = 'photo' | 'video';

class EditorStore {
  photoUrl: string | null = null
  mediaFile: File | null = null
  mediaType: ShootMediaType | null = null
  textboxes: Textbox[] = []
  selectedTextboxId: string | null = null
  isPosting = false
  error: string | null = null
  /** Optional registry link (POST form). */
  linkedFirearmModelId: string | null = null
  linkedCartridgeSpecId: string | null = null
  /** Intrinsic pixel width from loaded media; null until measured. */
  mediaIntrinsicWidth: number | null = null
  /** Intrinsic pixel height from loaded media; null until measured. */
  mediaIntrinsicHeight: number | null = null

  constructor () {
    makeAutoObservable(this)
  }

  /** Call when opening the shoots editor with a concrete file and preview URL. */
  beginShootEdit = (file: File, previewUrl: string, mediaType: ShootMediaType): void => {
    const sameMedia =
      this.mediaFile === file && this.photoUrl === previewUrl && this.mediaType === mediaType

    if (!sameMedia) {
      this.textboxes = []
      this.selectedTextboxId = null
      this.error = null
      this.linkedFirearmModelId = null
      this.linkedCartridgeSpecId = null
      this.mediaIntrinsicWidth = null
      this.mediaIntrinsicHeight = null
    }

    this.mediaFile = file
    this.photoUrl = previewUrl
    this.mediaType = mediaType
  }

  setLinkedFirearmModelId = (id: string | null): void => {
    this.linkedFirearmModelId = id
  }

  setLinkedCartridgeSpecId = (id: string | null): void => {
    this.linkedCartridgeSpecId = id
  }

  setMediaIntrinsicDimensions = (width: number, height: number): void => {
    runInAction(() => {
      this.mediaIntrinsicWidth = width
      this.mediaIntrinsicHeight = height
    })
  }

  createTextbox = (x: number, y: number, width = 0.4, height = 0.12): Textbox => {
    const textbox: Textbox = {
      id: uuid(),
      text: '',
      x,
      y,
      width,
      height,
      fontSize: 0.04,
      fontFamily: 'Arial',
      color: '#FFFFFF',
      opacity: 1,
      rotation: 0
    }
    this.textboxes.push(textbox)
    this.selectedTextboxId = textbox.id
    return textbox
  }

  updateTextbox = (id: string, updates: Partial<Omit<Textbox, 'id'>>): void => {
    const i = this.textboxes.findIndex((t) => t.id === id)
    if (i < 0) return
    const next = [...this.textboxes]
    next[i] = { ...next[i], ...updates }
    this.textboxes = next
  }

  updateFontSize = (id: string, size: number): void => {
    this.updateTextbox(id, { fontSize: size })
  }

  updateColor = (id: string, color: string): void => {
    this.updateTextbox(id, { color })
  }

  updateOpacity = (id: string, opacity: number): void => {
    this.updateTextbox(id, { opacity })
  }

  deleteTextbox = (id: string): void => {
    this.textboxes = this.textboxes.filter((t) => t.id !== id)
    if (this.selectedTextboxId === id) {
      this.selectedTextboxId = null
    }
  }

  selectTextbox = (id: string): void => {
    this.selectedTextboxId = id
  }

  deselectTextbox = (): void => {
    this.selectedTextboxId = null
  }

  setPhotoUrl = (url: string): void => {
    this.photoUrl = url
  }

  setMediaFile = (file: File): void => {
    this.mediaFile = file
  }

  /** Optional `thumbnailBlob` for video posts (first frame); caller may omit if extraction failed. */
  postShoot = async (onSuccess?: () => void, thumbnailBlob?: Blob): Promise<void> => {
    if (!this.canPost || !this.mediaFile || !this.mediaType) {
      runInAction(() => {
        this.error = 'Cannot post: no media selected'
      })
      return
    }

    runInAction(() => {
      this.isPosting = true
      this.error = null
    })

    try {
      const aspect = this.mediaAspect
      const aspectForJson = aspect ?? 1
      if (aspect == null) {
        console.warn('EditorStore.postShoot: mediaAspect was null; using fallback aspect 1.')
      }
      const textBoxesJson = serializeTextBoxes(this.textboxes, aspectForJson)

      const formData = new FormData()
      formData.append('mediaFile', this.mediaFile)
      formData.append('mediaType', this.mediaType)
      formData.append('textBoxesJson', textBoxesJson)

      if (thumbnailBlob) {
        formData.append('thumbnailFile', thumbnailBlob, 'thumbnail.jpg')
      }

      if (this.linkedFirearmModelId) {
        formData.append('firearmModelId', this.linkedFirearmModelId)
      }
      if (this.linkedCartridgeSpecId) {
        formData.append('cartridgeSpecId', this.linkedCartridgeSpecId)
      }

      await agent.Shoots.create(formData)

      runInAction(() => {
        this.clearEditingState()
        onSuccess?.()
      })
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'message' in err ? String((err as Error).message) : 'Upload failed'
      runInAction(() => {
        this.error = msg
      })
    } finally {
      runInAction(() => {
        this.isPosting = false
      })
    }
  }

  /** Clear overlays and errors; keep media and intrinsic dimensions for retry. */
  clearEditingState = (): void => {
    this.textboxes = []
    this.selectedTextboxId = null
    this.error = null
  }

  reset = (): void => {
    this.photoUrl = null
    this.mediaFile = null
    this.mediaType = null
    this.textboxes = []
    this.selectedTextboxId = null
    this.isPosting = false
    this.error = null
    this.linkedFirearmModelId = null
    this.linkedCartridgeSpecId = null
    this.mediaIntrinsicWidth = null
    this.mediaIntrinsicHeight = null
  }

  get selectedTextbox (): Textbox | null {
    if (!this.selectedTextboxId) return null
    return this.textboxes.find((t) => t.id === this.selectedTextboxId) ?? null
  }

  get canPost (): boolean {
    return this.photoUrl !== null && this.mediaFile !== null && this.mediaType !== null
  }

  /** Intrinsic width / height when both dimensions are known; else null. */
  get mediaAspect (): number | null {
    const w = this.mediaIntrinsicWidth
    const h = this.mediaIntrinsicHeight
    if (w == null || h == null) return null
    if (!(w > 0) || !(h > 0)) return null
    return w / h
  }
}

export default EditorStore
